import Mustache from 'mustache';

import {
  keyboardNavigable,
  nonRollingControlMode,
} from '../mixins/keyboard-navigable';

/**
 * Default value for how many search results are shown
 */
const SEARCH_RESULTS_LIMIT = 5;

/**
 * Defalt value how man old search results are shown
 */
const OLD_SEARCH_RESULT_LIMIT = 5;

/**
 * Key witch is used when old searches are stored to a local storage
 */
const LOCAL_STORAGE_KEY = 'free-search-field-search_v1';

/**
 * FreeSearchField
 */
class FreeSearchField {

  /**
   * @constructor
   * @param {HTMLElement} element element bind logic into
   * @param {object} options extra control parameters
   * @param {number} [options.searchResultLimit] how many search results are shown, defaults to SEARCH_RESULTS_LIMIT
   * @param {number} [options.oldSearchResultLimit] how many old search results are shown, defaults to OLD_SEARCH_RESULT_LIMIT
   * @param {string} [options.localStorageKey] key used to store old searches to localstorage, defaults to LOCAL_STORAGE_KEY
   * @param {function} [options.searcherFunction] search function
   */
  constructor(element, options = {}) {
    this._element = element;

    this._elements();
    this.bindKeyboardNavitable(element);
    this._bindEventListeners();

    this._options = {
      searchResultLimit: SEARCH_RESULTS_LIMIT,
      oldSearchResultLimit: OLD_SEARCH_RESULT_LIMIT,
      localStorageKey: LOCAL_STORAGE_KEY,
      ...options
    }

    window.addEventListener('click', event => {
      if (!event.target.closest('.free-search-field')) {
        this._closeComponent();
      }
    });
  }


  /**
   * Render free text results element with found results
   * Is callable from somewhere else.
   *
   * @param {string}   searchValue         - Search input value
   * @param {object[]} results             - Results array of objects
   * @param {string}   results[].keywords  - Keyword that is rendered as a search item. Mandatory.
   * @param {string}   results[].link      - Link that is rendered to link element. Mandatory.
   * @param {string}   [results[].results] - Results count that is rendered to element. Optional.
   */
  renderResults(searchValue, results) {
    const dataToRender = this._highLightSearchResults(searchValue, [ ...results ]).map(r => ({
      ...r,
      data: JSON.stringify(r)
    }));

    const html = Mustache.render(this._searchResultTemplate(), { results: dataToRender });
    this.elements.searchResults.innerHTML = html;
    this._updateFocusableElement();
    this.elements.searchResults.classList.toggle('hidden', dataToRender.length === 0);
    this.elements.noSearchResults.classList.toggle('hidden', dataToRender.length !== 0 || searchValue.length === 0);
    this._showOldSearches();
  }

  /**
   * Search element from DOM a access them later
   *
   * @private
   */
  _elements() {
    this.elements = {};
    this.elements.input = this._element.querySelector('input');
    this.elements.searchResults = this._element.querySelector('.search-result');
    this.elements.oldSearchResults = this._element.querySelector('.old-search-result');
    this.elements.oldSearchResultsArea = this._element.querySelector('.old-search-result-area');
    this.elements.noSearchResults = this._element.querySelector('.no-search-result');
    this._updateFocusableElement();
  }

  /**
   * Bind event listeners
   *
   * @private
   */
  _bindEventListeners() {
    this.elements.input.addEventListener('input', this._onSearch.bind(this));
    this.elements.input.addEventListener('focus', this._onSearch.bind(this));
    this._element.addEventListener('click', this._onClick.bind(this));
    this._element.addEventListener('blur', this._closeComponent.bind(this));

    // Add click event to each result
    this.elements.searchResults.addEventListener('click', this._resultLinkClick.bind(this));
    this.elements.oldSearchResults.addEventListener('click', this._resultLinkClick.bind(this));
  }

  /**
   * Result click event
   *
   * @param {Event} event Click event
   *
   * @private
   */
  _resultLinkClick(event) {
    event.preventDefault();
    const mainLinkElement = event.target.closest('a');

    // Dispatch custom event for clicking result link
    this._element.dispatchEvent(new CustomEvent('freeTextFieldResultClick', {
      detail: {
        linkElement: mainLinkElement
      }
    }));

    // Save search data to localStorage
    this._storeSearch(mainLinkElement);
    this._closeComponent();
    this.elements.input.value = '';
  }

  /**
   * Called from keyboard navigation when a element focus is lost
   *
   * @private
   */
  _tabOut() {
    this._closeComponent();
  }

  /**
   * Closes component menus
   *
   * @private
   */
  _closeComponent() {
    this.elements.searchResults.classList.toggle('hidden', true);
    this.elements.oldSearchResultsArea.classList.toggle('hidden', true);
  }

  /**
   * Store search for a later use
   * @param {Event} event event object
   * @private
   */
  _onClick(event) {
    if (event.target.tagName.toLowerCase() === 'a' && event.target.dataset.searchdata) {
      this._storeSearch(event.target);
    }
    if (event.target.tagName.toLowerCase() === 'input') {
      this.setFocusedElement(this.focusableElements[0]);
    }
  }

  /**
   * Called when a search result is clicked
   * @param {HTMLElement} link link element
   *
   * @private
   */
  _storeSearch(link) {
    try {
      const searchData = JSON.parse(link.dataset.searchdata);

      // Get previous searches and filter out ones that match the new search
      const previousSearches = this._fetchOldSearches().filter(search => JSON.stringify(search.selectedParams) !== JSON.stringify(searchData.selectedParams));

      const searches = [searchData, ...previousSearches].slice(0, this._options.oldSearchResultLimit);
      window.localStorage.setItem(this._options.localStorageKey, JSON.stringify(searches));
    } catch (error) {
      console.error('FreeSearchField - Could not parse search data from link object', error);
    }
  }

  /**
   * Update focusable element list for the keyboard navigation after each search.
   *
   * @private
   */
  _updateFocusableElement() {
    this.focusableElements = Array.from(this._element.querySelectorAll('input, .search-result a, .old-search-result a'));
  }

  /**
   * Highlight matching part search result text before it's shown in UI
   * @param {string} searchValue search string
   * @param {object[]} results search results
   * @returns {object[]} highlighed search results
   * @private
   */
  _highLightSearchResults(searchValue, results) {
    const regularExpression = new RegExp('(' + searchValue + ')', 'gi');

    return results.map(result => {
      result.keywords = result.keywords.replace(regularExpression, '<span class="matching-keywords">$1</span>');
      return result;
    });
  }

  /**
   * Called when a user types something in the input field
   * Fires custom event called freeTextFieldInput. Catch this via addEventListener
   * @param {string} event input event
   * @private
   * @async
   */
  async _onSearch(event) {
    this._element.dispatchEvent(new CustomEvent('freeTextFieldInput', {
      detail: {
        value: event.target.value.toLowerCase()
      }
    }));
  }

  /**
   * Fetch old searches
   * @returns {object[]} search results
   * @private
   */
  _fetchOldSearches() {
    let searches = window.localStorage.getItem(this._options.localStorageKey);
    return searches ? JSON.parse(searches) : [];
  }

  /**
   * Show old searches in UI
   * @private
   */
  _showOldSearches() {
    try {
      //Don't fill data attribute for results, this way old searched are not save again to local storage when user clicks old search
      const results = this._fetchOldSearches().map(r => ({
        ...r,
        data: JSON.stringify(r)
      }))
      const html = Mustache.render(this._searchResultTemplate(), { results });
      this.elements.oldSearchResults.innerHTML = html;
      this.elements.oldSearchResultsArea.classList.toggle('hidden', results.length === 0);
      this._updateFocusableElement();
    } catch (error) {
      console.error('FreeSearchField - could not load old searches', error);
    }
  }

  /**
   * Search result template
   * @returns {string} template
   * @private
   */
  _searchResultTemplate() {
    //Keep ' markers around data-searchdata='' intact.. otherwise you brake the JSON enconding
    return `{{#results}}
      <a href='{{link}}' data-searchdata='{{{data}}}' tabindex="-1" class='zeta'>
        <span>{{{keywords}}}</span>
        {{#results}}<span class="results-count">{{results}}</span>{{/results}}
      </a>
    {{/results}}`;
  }
}

//Assign mixins to make element keyboard navigable
Object.assign(FreeSearchField.prototype, keyboardNavigable, nonRollingControlMode);

export {
  FreeSearchField
}
